import * as THREE from "three";
import {
  getCameraProbeMarkerActions,
  getCameraProbeMarkerState,
  useCameraProbeMarkerStore,
} from "../stores/cameraProbeMarkerStore";
import { logger } from "../utils/logger";

const CAMERA_PROBE_MARKER_NAME = "__cameraProbeMarker";

type MarkerWorld = [number, number, number];
type SceneRef = { current?: THREE.Scene | null } | null | undefined;
type MaybeScene = THREE.Scene | null | undefined;
type MaybeWorld = MarkerWorld | null | undefined;

export function ensureCameraProbeMarker(scene: MaybeScene): THREE.Object3D | null {
  if (!scene) return null;
  let marker = scene.getObjectByName(CAMERA_PROBE_MARKER_NAME);
  if (!marker) {
    const geometry = new THREE.SphereGeometry(0.03, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0xff4d8d,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.9,
    });
    marker = new THREE.Mesh(geometry, material);
    marker.name = CAMERA_PROBE_MARKER_NAME;
    marker.renderOrder = 9999;
    marker.visible = false;
    scene.add(marker);
  }
  try {
    marker.userData = {
      ...(marker.userData || {}),
      isCameraProbeMarker: true,
      type: "cameraProbeMarker",
    };
  } catch {
    // ignore
  }
  return marker;
}

export function removeCameraProbeMarker(scene: MaybeScene): void {
  if (!scene) return;
  const marker = scene.getObjectByName(CAMERA_PROBE_MARKER_NAME) as THREE.Mesh | null;
  if (marker) {
    try {
      marker.parent?.remove(marker);
    } catch {
      // ignore
    }
    try {
      marker.geometry.dispose();
      const mat = marker.material;
      if (mat) {
        if (Array.isArray(mat)) {
          mat.forEach((m) => m?.dispose && m.dispose());
        } else if (typeof mat.dispose === "function") {
          mat.dispose();
        }
      }
    } catch (error) {
      logger.warn("[Scene] Failed to dispose camera probe marker resources", error);
    }
  }
  getCameraProbeMarkerActions().clearMarkerWorld();
}

export function setCameraProbeMarker(scene: MaybeScene, world: MaybeWorld): void {
  if (!scene) return;
  if (!Array.isArray(world) || world.length < 3) return;
  try {
    const marker = ensureCameraProbeMarker(scene) as THREE.Mesh | null;
    if (!marker) return;
    marker.position.set(Number(world[0]) || 0, Number(world[1]) || 0, Number(world[2]) || 0);
    marker.visible = getCameraProbeMarkerState().markerVisible === true;
  } catch (error) {
    logger.warn("[Scene] Unable to show camera probe marker from event", error);
  }
}

export function setupCameraProbeMarkerBridge(sceneRef: SceneRef): () => void {
  const renderFromStore = () => {
    const scene = sceneRef?.current;
    if (!scene) return;
    const state = getCameraProbeMarkerState();
    const world = state.markerWorld;
    if (Array.isArray(world) && world.length >= 3) {
      setCameraProbeMarker(scene, world);
      return;
    }
    const marker = scene.getObjectByName(CAMERA_PROBE_MARKER_NAME);
    if (marker) marker.visible = false;
  };

  const markerWorldHandler = (event: Event) => {
    const world = (event as CustomEvent<{ world?: MaybeWorld }>).detail?.world;
    getCameraProbeMarkerActions().setMarkerWorld(
      Array.isArray(world) && world.length >= 3 ? world : null
    );
  };

  const markerClearHandler = () => {
    try {
      removeCameraProbeMarker(sceneRef?.current);
    } catch (error) {
      logger.warn("[Scene] Unable to clear camera probe marker", error);
    }
  };

  const markerVisibilityHandler = (event: Event) => {
    const visible = (event as CustomEvent<{ visible?: boolean }>).detail?.visible === true;
    getCameraProbeMarkerActions().setMarkerVisible(visible);
    try {
      const scene = sceneRef?.current;
      if (!scene) return;
      const marker = scene.getObjectByName(CAMERA_PROBE_MARKER_NAME);
      if (marker) {
        marker.visible = visible;
      } else if (visible) {
        const cached = getCameraProbeMarkerState().markerWorld;
        if (cached) setCameraProbeMarker(scene, cached);
      }
    } catch (error) {
      logger.warn("[Scene] Unable to apply camera probe marker visibility", error);
    }
  };

  let prevState = getCameraProbeMarkerState();
  const unsubscribeStore = useCameraProbeMarkerStore.subscribe((state) => {
    const nextMarkerWorld = state.markerWorld;
    const nextMarkerVisible = state.markerVisible;
    const markerWorldChanged = nextMarkerWorld !== prevState.markerWorld;
    const markerVisibleChanged = nextMarkerVisible !== prevState.markerVisible;
    prevState = state;
    if (markerWorldChanged) {
      renderFromStore();
      return;
    }
    if (!markerVisibleChanged) return;
    try {
      const scene = sceneRef?.current;
      if (!scene) return;
      const marker = scene.getObjectByName(CAMERA_PROBE_MARKER_NAME);
      if (marker) {
        marker.visible = nextMarkerVisible;
      } else if (nextMarkerVisible) {
        renderFromStore();
      }
    } catch (error) {
      logger.warn("[Scene] Unable to apply camera probe marker visibility", error);
    }
  });

  renderFromStore();
  window.addEventListener("camera-probe-marker-world", markerWorldHandler);
  window.addEventListener("camera-probe-marker-clear", markerClearHandler);
  window.addEventListener("camera-probe-marker-visibility", markerVisibilityHandler);

  return () => {
    unsubscribeStore();
    window.removeEventListener("camera-probe-marker-world", markerWorldHandler);
    window.removeEventListener("camera-probe-marker-clear", markerClearHandler);
    window.removeEventListener("camera-probe-marker-visibility", markerVisibilityHandler);
  };
}
